import { getOfflineDb } from './offlineDb';
import { getOperation } from './operationRegistry';
import { computeCollapsedIds } from './collapseRules';
import { ApiError } from '../api';

export interface SyncCallbacks {
  onSyncComplete: () => void;
  onSyncError: (failedCount: number) => void;
}

let isSyncing = false;

export async function processSyncQueue(callbacks: SyncCallbacks): Promise<boolean> {
  if (isSyncing) return false;
  isSyncing = true;

  try {
    const db = getOfflineDb();
    if (!db) {
      return false;
    }

    const pending = await db.syncQueue
      .where('status')
      .equals('pending')
      .sortBy('createdAt');

    if (pending.length === 0) {
      return false;
    }

    const idsToDelete = computeCollapsedIds(pending);
    if (idsToDelete.length > 0) {
      await db.syncQueue.bulkDelete(idsToDelete);
    }

    const tempIdMap = new Map<string, number>();
    let failedCount = 0;

    while (true) {
      const next = await db.syncQueue
        .where('status')
        .equals('pending')
        .sortBy('createdAt')
        .then((entries) => entries[0]);

      if (!next) break;

      await db.syncQueue.update(next.localId!, { status: 'processing' });

      const resolved = resolvePayload(next.payload, tempIdMap);

      try {
        const op = getOperation(next.type);
        const result = await op.execute(resolved, tempIdMap);

        if (next.tempId && result?.id) {
          const realId = op.extractId ? op.extractId(result) : result.id;
          tempIdMap.set(next.tempId, realId);
          op.onIdResolved?.(next.tempId, realId);
        }

        await db.syncQueue.delete(next.localId!);
        window.dispatchEvent(new Event('sync-queue-changed'));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          await db.syncQueue.update(next.localId!, { status: 'pending' });
          break;
        }

        // Network-level failure (fetch TypeError): the backend was never
        // reached — e.g. navigator.onLine misreports online on a cold start
        // in an already-offline browser (VIVATISPDB-435). Keep the operation
        // pending and stop; the next flush retries the whole queue.
        if (err instanceof TypeError) {
          await db.syncQueue.update(next.localId!, { status: 'pending' });
          break;
        }

        const message = err instanceof Error ? err.message : 'Unknown error';
        await db.syncQueue.update(next.localId!, {
          status: 'failed',
          error: message,
        });
        failedCount++;
      }
    }

    if (failedCount > 0) {
      callbacks.onSyncError(failedCount);
    } else {
      callbacks.onSyncComplete();
    }

    return true;
  } finally {
    isSyncing = false;
  }
}

function resolvePayload(
  payload: Record<string, unknown>,
  tempIdMap: Map<string, number>,
): Record<string, unknown> {
  const resolved = { ...payload };

  for (const [key, value] of Object.entries(resolved)) {
    if (typeof value === 'string' && value.startsWith('temp_')) {
      const realId = tempIdMap.get(value);
      if (realId !== undefined) {
        resolved[key] = realId;
      }
    }
  }

  return resolved;
}

export function isSyncInProgress(): boolean {
  return isSyncing;
}
